import React, { useEffect, useState } from "react";
import { Target, Users, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg, fmtINR } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";

export const TARGET_STATUS_META = {
  achieved: { label: "Achieved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  on_track: { label: "On track", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  behind: { label: "Behind pace", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  no_target: { label: "No target", cls: "bg-muted text-muted-foreground" },
};

/**
 * Municipal Admin sets a supervisor's monthly Personal target (vs own collections) and
 * Team goal (vs team collections). `supervisor` = {id, name, targets?: {...}} — `targets` may carry the
 * current values for the month being edited.
 */
export function SupervisorTargetDialog({ code, supervisor, month: monthProp, onOpenChange, onSaved }) {
  const [month, setMonth] = useState(monthProp || new Date().toISOString().slice(0, 7));
  const [personal, setPersonal] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [ctx, setCtx] = useState(null);   // row from the API for the selected month

  useEffect(() => { if (monthProp) setMonth(monthProp); }, [monthProp]);

  // load the supervisor's row for the chosen month so the form shows current values + collections
  useEffect(() => {
    if (!supervisor) return;
    setCtx(null);
    api.get(`/t/${code}/supervisor-targets`, { params: { month } })
      .then((r) => {
        const row = (r.data.supervisors || []).find((s) => s.supervisor_id === supervisor.id) || null;
        setCtx(row);
        setPersonal(row?.personal_target != null ? String(row.personal_target) : "");
        setGoal(row?.team_goal != null ? String(row.team_goal) : "");
      })
      .catch(() => { setPersonal(""); setGoal(""); });
  }, [code, supervisor, month]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/t/${code}/supervisor-targets/${supervisor.id}`, {
        month, personal_target: personal === "" ? null : Number(personal), team_goal: goal === "" ? null : Number(goal),
      });
      toast.success(`Targets for ${supervisor.name} saved for ${month}`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!supervisor} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="supervisor-target-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <Target className="h-4 w-4" /> Supervisor targets — {staffLabelText(supervisor?.name, supervisor?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
              className="cursor-pointer" data-testid="supervisor-target-month" />
          </div>

          <div className="rounded-lg border p-3 space-y-2" data-testid="supervisor-target-personal-block">
            <Label className="flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5" /> Personal target (₹)</Label>
            <Input type="number" min="0" step="100" value={personal} onChange={(e) => setPersonal(e.target.value)}
              placeholder="e.g. 50000" data-testid="supervisor-target-personal-input" />
            <p className="text-[11px] text-muted-foreground">
              Measured against the supervisor's <b>own</b> collections during field visits.
              {ctx && <> Collected so far this month: <b>{fmtINR(ctx.personal_collected)}</b> ({ctx.personal_receipts} receipt{ctx.personal_receipts === 1 ? "" : "s"}).</>}
            </p>
          </div>

          <div className="rounded-lg border p-3 space-y-2" data-testid="supervisor-target-goal-block">
            <Label className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Team goal (₹)</Label>
            <Input type="number" min="0" step="1000" value={goal} onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g. 250000" data-testid="supervisor-target-goal-input" />
            <p className="text-[11px] text-muted-foreground">
              Top-down goal for the whole team's collections.
              {ctx && <> Team has collected <b>{fmtINR(ctx.team_collected)}</b>; bottom-up team target (sum of collector targets) is <b>{fmtINR(ctx.team_target)}</b>.</>}
            </p>
          </div>

          <p className="text-[11px] text-muted-foreground">Leave a field blank (or 0) to clear that target.</p>
          <Button type="submit" className="w-full" disabled={busy} data-testid="supervisor-target-save-button">
            {busy ? "Saving..." : "Save targets"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
